import React, { useState } from "react";
import { CloudUploadOutlined, CloseOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd";
import { Upload } from "antd";
import { openNotification } from "../../Notification/Index";
import "./dragger.scss";
import { getFileMimeTypeImage } from "../../../Utils/generic-functions";

const { Dragger } = Upload;

interface DraggerAreaProps {
  handleFile: (file: File) => void;
  disabled: boolean;
  handleCancelFile: () => void;
}

const DraggerArea: React.FC<DraggerAreaProps> = ({
  handleFile,
  disabled,
  handleCancelFile,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // Helper function to validate file type
  const validateFileType = (file: File) => {
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    return validTypes.includes(file.type);
  };

  const uploadResumeFile = async (options: any) => {
    const { file, onSuccess, onError } = options;

    if (!validateFileType(file as File)) {
      openNotification(
        "error",
        "Upload Error",
        "Invalid file type. Only PDF, DOC, and DOCX files are allowed."
      );
      onError?.("Invalid file type");
      return;
    }

    try {
      handleFile(file as File);
      onSuccess?.();
    } catch (error) {
      onError?.(error);
    }
  };

  const draggerProps = {
    name: "file",
    multiple: false,
    customRequest: uploadResumeFile,
    // accept: ".pdf, .doc, .docx", temporarily disabled docx and doc
    accept:".pdf",
    onChange(info: any) {
      let newFileList = [...info.fileList];
      const { status } = info.file;

      if (status === "done" || status === "error") {
        setFileList(newFileList);
      }

      if (status === "error") {
        openNotification("error", "Upload Error", "File upload failed.");
      }
    },
    onDrop(e: any) {
      console.log("Dropped files", e.dataTransfer.files);
    },
    disabled,
  };

  const handleRemove = (file: UploadFile) => {
    setFileList(fileList.filter((f) => f.uid !== file.uid));
    handleCancelFile();
  };

  return (
    <div>
      {fileList.length === 0 ? (
        <Dragger {...draggerProps} disabled={disabled}>
          <p className="ant-upload-drag-icon">
            <CloudUploadOutlined />
          </p>
          <p className="ant-upload-text">
            Drag your file(s) or{" "}
            {/* <span style={{ color: "#098586" }}>browse</span> */}
            <span>browse</span>
          </p>
          <p className="ant-upload-hint">
            {/* Upload PDF, DOC, and DOCX files only. Max 10 MB files are allowed. */}
            Upload PDF formatted files only. Max 10 MB files are allowed.
          </p>
        </Dragger>
      ) : (
        <div className="upload-preview">
          {fileList.map((file) => (
            <div key={file.uid} className="upload-preview-item">
              <div className="upload-preview-image-wrapper">
                <img
                  src={getFileMimeTypeImage(file?.name || "pdf")}
                  alt={file.name}
                  className="upload-preview-image"
                />
                <CloseOutlined
                  onClick={() => handleRemove(file)}
                  className="upload-preview-cancel"
                />
              </div>
              <span className="upload-preview-name">{file.name}</span>
              <span className="upload-preview-size">
                {file.size
                  ? `${(file.size / 1024).toFixed(2)} KB`
                  : "Unknown size"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DraggerArea;
